import React, { useEffect, useState, useRef } from "react";
import { useTheme, styled } from "@mui/material/styles";
import { Tooltip, Box, Typography } from "@mui/material";
import * as echarts from "echarts";

import item_primary_icn from "assets/images/icons/item_primary.svg";
import item_blue_light_icn from "assets/images/icons/item_blue_light.svg";
import item_green_primary_icn from "assets/images/icons/item_green_primary.svg";
import item_yellow_light_icn from "assets/images/icons/item_yellow_light.svg";
import item_green_light_icn from "assets/images/icons/item_green_light.svg";
import item_sliver_icn from "assets/images/icons/item_sliver.svg";

const tooltipUnit = {
  accessPointsOverviewClient: "Clients",
  accessPointsOverviewTraffic: "GB",
  wirelessClientsBand: "Clients",
};

const ScrollTooltipBox = styled(Box)(({ theme }) => ({
  maxHeight: '110px',
  overflowY: 'auto',
  paddingRight: '4px',

  "&::-webkit-scrollbar": {
    width: 5,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.primary.main,
    borderRadius: 8,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: theme.palette.primary.mainHover,
    cursor: "ew-resize",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.primary.greyThread,
    borderRadius: theme.shape.customBorderRadius[0],
  },
  "&::-webkit-scrollbar-button": {
    display: "none",
    width: 0,
    height: 0,
  },
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.customFontSize[1],
  color: theme.palette.primary.white,
}));

const getLayoutConfig = (data, width, groupCount) => {
  const isPlural = groupCount > 1 ? true : false;

  if (width < 600) {
    return {
      radius: ["55%", "72%"],
      centers: data.map((_, i) => [`${25 + i * 45}%`, isPlural ? "40%" : "50%"]),
      legend: {
        ...(isPlural ? {
          orient: "horizontal",
          bottom: -5,
          left: "center",
        } : {
          orient: "vertical",
          right: "5%",
          top: "center",
        }),
      },
    };
  }

  return {
    radius: ["60%", "75%"],
    centers: data.map((_, i) => [`${30 + i * 25}%`, "50%"]),
    legend: {
      orient: "vertical",
      right: isPlural ? "10%" : "15%",
      top: "center",
    },
  };
};

const iconStyleSheet = {
  primary: item_primary_icn,
  buleLight: item_blue_light_icn,
  greenPrimary: item_green_primary_icn,
  yellowLight: item_yellow_light_icn,
  greenLight: item_green_light_icn,
  sliver: item_sliver_icn
};

const PieChart = ({ pieGroupsData, height, formattedLegendName, handleLegendOnClick }) => {
  const theme = useTheme();
  const pieStyleSheet = {
    primary: theme.palette.primary.main,
    buleLight: theme.palette.primary.blueSecound,
    greenPrimary: theme.palette.primary.greenPrimary,
    yellowLight: theme.palette.primary.yellowPrimary,
    greenLight: theme.palette.primary.greenLight,
    sliver: theme.palette.primary.greyThread
  };

  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [tooltipLocked, setTooltipLocked] = useState(false);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom) return;

    const width = chartDom.clientWidth;
    const layout = getLayoutConfig(pieGroupsData, width, pieGroupsData.length);
    const totals = pieGroupsData.map(group => {
      const sum = group.data.reduce((sum, item) => sum + item.value, 0);
      return Number(sum.toFixed(2));
    });

    const chart = echarts.init(chartDom, null, {
      renderer: "svg",
      useDirtyRect: true,
    });
    chartInstance.current = chart;

    const option = {
      legend: {
        ...layout.legend,
        selectedMode: true,
        hoverLink: true,
        data: pieGroupsData[0].data.map((item) => ({
          name: item.name,
          icon: `image://${iconStyleSheet[item.type]}`,
        })),
        textStyle: {
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.primary.greyPrimary,
          fontSize: theme.typography.customFontSize[3],
          rich: {
            left: {
              fontSize: theme.typography.customFontSize[3],
              color: theme.palette.primary.greyPrimary,
              width: 100,
              align: 'left',
              padding: [0, 10, 0, 0],
            },
            right: {
              fontSize: theme.typography.customFontSize[3],
              fontWeight: 600,
              color: theme.palette.primary.main,
              width: 50,
              align: 'right',
            },
            unit: {
              fontSize: theme.typography.customFontSize[4],
              color: theme.palette.primary.greyThird,
              width: 50,
              align: 'left',
            },
            rssiFiftyFiveLeft: {
              fontSize: theme.typography.customFontSize[4],
              fontWeight: theme.typography.customFontWeight[1],
              color: theme.palette.primary.greyPrimary,
              width: 60,
              align: 'left',
            },
            rssiRangeLeft: {
              fontSize: theme.typography.customFontSize[4],
              fontWeight: theme.typography.customFontWeight[1],
              color: theme.palette.primary.greyPrimary,
              width: 30,
              align: 'left',
            },
            rssiRangeRight: {
              fontSize: theme.typography.customFontSize[3],
              fontWeight: 600,
              color: theme.palette.primary.main,
              width: 25,
              align: 'left',
            },
            rssiSeventyLeft: {
              fontSize: theme.typography.customFontSize[4],
              fontWeight: theme.typography.customFontWeight[1],
              color: theme.palette.primary.greyPrimary,
              width: 30,
              align: 'left',
            },
            rssiDbm: {
              fontSize: theme.typography.customFontSize[3],
              color: theme.palette.primary.greyThird,
              width: 50,
              align: 'left',
            },
          },
        },
        formatter: (name) => {
          if (!formattedLegendName) return name;

          const { [name]: value = "-", unit = "-", key } = formattedLegendName;

          const isRssi = key === "rssi";
          const rssiFormatterName = {
            ">= -55 dBm": `{rssiFiftyFiveLeft|>= -55}{rssiDbm|dBm} {right|${value}}{unit|${unit}}`,
            "-70 to -55 dBm": `{rssiRangeLeft|-70} to {rssiRangeLeft|-55} {rssiDbm|dBm}    {rssiRangeRight|${value}}{unit|${unit}}`,
            "< -70 dBm": `{rrssiSeventyLeft|< -70} {rssiDbm|dBm}      {right|${value}}{unit|${unit}}`,
          }

          return isRssi ? rssiFormatterName[name] : `{left|${name}} {right|${value}} {unit|${unit}}`;
        }
      },
      tooltip: { show: false },
      series: pieGroupsData.map((group, index) => ({
        name: group.key,
        type: "pie",
        radius: layout.radius,
        center: layout.centers[index],
        avoidLabelOverlap: true,
        itemStyle: {
          borderColor: theme.palette.primary.white,
          borderWidth: 2,
        },
        label: {
          show: true,
          position: "center",
          formatter: () => `{total|${totals[index]}}\n{desc|${group.label}}`,
          rich: {
            total: {
              fontSize: theme.typography.customFontSize[11],
              fontWeight: theme.typography.customFontWeight[1],
              color: theme.palette.primary.greyPrimary,
            },
            desc: {
              fontSize: theme.typography.customFontSize[0],
              fontWeight: theme.typography.customFontWeight[0],
              color: theme.palette.primary.greySeventh,
            },
          },
        },
        data: group.data.map((item) => ({
          value: item.value,
          name: item.name,
          itemStyle: {
            color: pieStyleSheet[item.type],
          },
        })),
      })),
    };

    chart.setOption(option);

    // legend 點擊事件
    chart.on("legendselectchanged", (event) => {
      Object.keys(event.selected).forEach((name) => {
        chart.dispatchAction({
          type: 'legendSelect',
          name,
        });
      });

      const target = pieGroupsData
        .find(item => item.key === "accessPointsOverviewClient")
        ?.data.find(d => d.name === event.name);

      if (target && handleLegendOnClick) {
        const currentDeviceId = target.deviceId
        handleLegendOnClick("devices", true, currentDeviceId);
      }
    });

    // 圓餅圖 hover
    chart.on("mouseover", (params) => {
      if (params.componentType !== "series" || params.value === 0) return;
      if (!tooltipLocked && params.event) {
        setTooltipPos({
          x: params.event.offsetX,
          y: params.event.offsetY - 15,
        });
        setTooltipLocked(true);
        setTooltipData(params);
      }
    });

    chart.on("mouseout", () => {
      setTooltipLocked(false);
      setTooltipData(null);
      chart.dispatchAction({ type: "downplay" });
    });

    const resizeObserver = new ResizeObserver(() => {
      const width = chartDom.clientWidth;
      const layout = getLayoutConfig(pieGroupsData, width, pieGroupsData.length);

      const textStyle = {
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.primary.greyPrimary,
        fontSize: (width < 500 && pieGroupsData.length > 1)
          ? theme.typography.customFontSize[0]
          : theme.typography.customFontSize[3],
        width: width < 500 ? (pieGroupsData.length > 1 ? 53.8 : 100)
          : (pieGroupsData.length > 1 ? 80 : 250),
        overflow: 'truncate',
      };

      chart.setOption({
        legend: {
          ...layout.legend,
          textStyle,
        },
        series: pieGroupsData.map((_, index) => ({
          center: layout.centers[index],
          radius: layout.radius,
        })),
      });
      chart.resize();
    });

    resizeObserver.observe(chartDom);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [pieGroupsData, theme, tooltipData]);

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <div
        ref={chartRef}
        style={{
          width: "100%",
          height,
          background: theme.palette.primary.white,
        }}
      />
      {tooltipData && (
        <Tooltip
          open
          arrow
          PopperProps={{
            onMouseLeave: () => {
              setTooltipLocked(false);
              setTooltipData(null);

              chartInstance.current?.dispatchAction({
                type: "downplay",
              });
            },
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 8],
                },
              },
            ],
          }}
          title={
            tooltipData.seriesName === "rssi" ? (
              <ScrollTooltipBox>
                {pieGroupsData
                  .find((group) => group.key === "rssi")
                  ?.data.find((item) => item.name === tooltipData.name)
                  ?.clientList?.map((client) => (
                    <Typography
                      key={client.clientId}
                      sx={{
                        fontSize: theme.typography.customFontSize[1],
                        lineHeight: 1.69,
                        color: theme.palette.primary.white,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {client.clientName || client.clientId}
                    </Typography>
                  ))}
              </ScrollTooltipBox>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <StyledTypography
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "200px",
                    cursor: "default",
                  }}
                >
                  {tooltipData.name}
                </StyledTypography>
                <StyledTypography sx={{ marginLeft: 0.5 }}>:</StyledTypography>
                <StyledTypography
                  sx={{
                    fontWeight: theme.typography.customFontWeight[1],
                    marginLeft: 1,
                  }}
                >
                  {tooltipUnit[tooltipData.seriesName] === "Clients"
                    ? tooltipData.value
                    : tooltipData.value.toFixed(2)}
                </StyledTypography>
                <StyledTypography sx={{ marginLeft: 1 }}>
                  {tooltipUnit[tooltipData.seriesName]}
                </StyledTypography>
              </Box>
            )
          }
        >
          <div
            style={{
              position: "absolute",
              left: tooltipPos.x,
              top: tooltipPos.y,
              width: 1,
              height: 1,
            }}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default PieChart;